/**
 * Domain types for the trip itinerary.
 *
 * These mirror the shape of `src/data/itinerary.json`. The runtime validator
 * in `schema.ts` guarantees loaded data actually conforms to these types, so
 * the rest of the app can treat them as trustworthy.
 */

/** The four visual/semantic categories a stop or day can have. */
export type Category = 'attraction' | 'nature' | 'transfer' | 'hotel';

/** All valid category values, for validation and iteration. */
export const CATEGORIES: readonly Category[] = ['attraction', 'nature', 'transfer', 'hotel'];

/** A single place to visit within a day. */
export interface Stop {
  name: string;
  category: Category;
  /** Local clock time "HH:MM" (optional — some stops are flexible). */
  time?: string;
  lat: number;
  lng: number;
  /** Google Maps Place ID, when known — improves navigation accuracy. */
  placeId?: string;
  /** Optional official website. */
  url?: string;
  /** Hebrew free-text note shown on the detail card. */
  note?: string;
}

/** One day of the itinerary. */
export interface Day {
  /** 1-based day number. */
  n: number;
  /** ISO date, "YYYY-MM-DD". */
  date: string;
  /** Hebrew weekday name. */
  dow: string;
  /** Hebrew day title. */
  title: string;
  /** Dominant category for the day (drives the day-list color dot). */
  category: Category;
  stops: Stop[];
}

/** A lodging record, pinned across its night range. */
export interface Hotel {
  name: string;
  /** Human-readable night range, e.g. "23–27/7". */
  nights: string;
  lat: number;
  lng: number;
  placeId?: string;
  note?: string;
}

export interface Flight {
  no: string;
  from: string;
  to: string;
  /** ISO datetime, local. */
  dep: string;
  arr: string;
}

export interface TripMeta {
  title: string;
  /** ISO date "YYYY-MM-DD". */
  start: string;
  end: string;
  flights: {
    outbound: Flight;
    inbound: Flight;
  };
}

/** The full itinerary document. */
export interface Itinerary {
  trip: TripMeta;
  hotels: Hotel[];
  days: Day[];
}

/** Sentinel for "show every day" in the day filter. */
export const ALL_DAYS = 'all' as const;
export type DaySelection = typeof ALL_DAYS | number;
